'use client'

import React from 'react'
import Link from 'next/link'
import { Merchant } from '@/services/dataTypes'
import { getMerchantHref } from '@/constants/hooks'

interface Props {
    merchant: Merchant
    categoryName: string
    categorySlug: string
    storeSlug: string
    slugType: string
    totalOffers: number
    lastUpdated: string
    similarMerchants: Merchant[]
    siteName?: string
}

const StoreSidebar = ({
    merchant,
    categoryName,
    categorySlug,
    storeSlug,
    slugType,
    totalOffers,
    lastUpdated,
    similarMerchants = [],
    siteName = ''
}: Props) => {
    // Clean website url to show just hostname (e.g. acurite.com)
    const displayDomain = merchant?.website_url
        ? merchant.website_url.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/.*$/, '')
        : 'Official Website'

    return (
        <aside className="space-y-6">
            {/* ── STORE INFO CARD ── */}
            <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs">
                <h3 className="text-base font-bold text-[#071822] mb-4">
                    {merchant?.merchant_name} Store Info
                </h3>

                <dl className="space-y-3 text-xs">
                    <div className="flex items-start justify-between gap-2 pb-2.5 border-b border-slate-100">
                        <dt className="text-slate-500 font-medium">Website</dt>
                        <dd className="font-semibold text-right">
                            {merchant?.website_url ? (
                                <Link
                                    href={merchant.website_url}
                                    target="_blank"
                                    rel="nofollow noopener noreferrer"
                                    className="text-slate-800 hover:text-[#00875a] transition-colors truncate max-w-[170px] inline-block"
                                >
                                    {displayDomain}
                                </Link>
                            ) : (
                                <span className="text-slate-400">N/A</span>
                            )}
                        </dd>
                    </div>

                    <div className="flex items-start justify-between gap-2 pb-2.5 border-b border-slate-100">
                        <dt className="text-slate-500 font-medium">Category</dt>
                        <dd className="font-semibold text-right">
                            <Link
                                href={`/${categorySlug}`}
                                className="text-slate-800 hover:text-[#00875a] transition-colors text-right"
                            >
                                {categoryName}
                            </Link>
                        </dd>
                    </div>

                    <div className="flex items-start justify-between gap-2 pb-2.5 border-b border-slate-100">
                        <dt className="text-slate-500 font-medium">Active offers</dt>
                        <dd className="font-bold text-slate-800">{totalOffers}</dd>
                    </div>

                    <div className="flex items-start justify-between gap-2">
                        <dt className="text-slate-500 font-medium">Last updated</dt>
                        <dd className="font-medium text-slate-600">{lastUpdated}</dd>
                    </div>
                </dl>

                {merchant?.website_url && (
                    <div className="mt-5">
                        <Link
                            href={merchant.website_url}
                            target="_blank"
                            rel="nofollow noopener noreferrer"
                            className="w-full py-2.5 bg-[#071822] hover:bg-[#00d69f] hover:text-[#071822] text-white text-xs font-bold rounded-xl text-center block transition-all shadow-xs"
                        >
                            Visit {merchant.merchant_name}
                        </Link>
                    </div>
                )}
            </div>

            {/* ── AFFILIATE DISCLOSURE ── */}
            <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs space-y-2">
                <h4 className="text-sm font-bold text-[#071822]">
                    Affiliate Disclosure
                </h4>
                <p className="text-xs text-slate-800 leading-relaxed font-normal">
                    {siteName} may earn a commission when you buy through links on this page. This does not change the price you pay.{' '}
                    <Link href="/affiliate-disclosure" className="text-slate-800 hover:text-[#00875a] font-semibold underline underline-offset-2">
                        Read more
                    </Link>
                </p>
            </div>

            {/* ── MORE IN CATEGORY ── */}
            {similarMerchants?.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs space-y-3">
                    <h4 className="text-sm font-bold text-[#071822]">
                        Similar Stores
                    </h4>
                    <div className="space-y-2.5">
                        {similarMerchants.filter((m: Merchant) => m.unique_id !== merchant?.unique_id).slice(0, 5).map((m: Merchant) => {
                            return (
                                <Link
                                    key={m.unique_id}
                                    href={getMerchantHref(m, storeSlug, slugType)}
                                    className="block text-xs font-medium text-slate-700 hover:text-[#00875a] transition-colors"
                                >
                                    {m.merchant_name} coupon codes
                                </Link>
                            )
                        })}
                    </div>
                    <div className="pt-2 border-t border-slate-100">
                        <Link
                            href={`/${categorySlug}`}
                            className="text-xs font-semibold text-[#00875a] hover:underline inline-flex items-center gap-1"
                        >
                            <span>View all {categoryName.toLowerCase()} coupons</span>
                            <span>→</span>
                        </Link>
                    </div>
                </div>
            )}
        </aside>
    )
}

export default StoreSidebar
